// Slide-from-right Drawer mit allen Details + Tier-Change + Delete-CTA.
// Klick auf Backdrop schließt. ESC schließt.

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, X } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { adjustUserCredits } from '@/lib/admin-credits-api';
import { CREDITS_CHANGED_EVENT } from '@/hooks/use-credits';
import type { AdminUserRow, UserRole } from '@/lib/admin-users-api';

const ROLE_OPTIONS: ReadonlyArray<{ key: UserRole; label: string }> = [
  { key: 'free', label: 'Free' },
  { key: 'trial', label: 'Trial' },
  { key: 'pro', label: 'Pro' },
  { key: 'owner', label: 'Owner' },
];

interface UserDetailDrawerProps {
  user: AdminUserRow | null;
  onClose: () => void;
  onSaveTier: (userId: string, role: UserRole) => Promise<void>;
  onDelete: (userId: string) => void;
  onActivateCoaching: (userId: string) => void;
  onCancelCoaching: (userId: string) => Promise<void>;
  /** Wird nach erfolgreichem Credit-Grant/Deduct aufgerufen (Liste reloaden). */
  onCreditsChanged?: (userId: string, newBalance: number) => void;
}

export function UserDetailDrawer({
  user,
  onClose,
  onSaveTier,
  onDelete,
  onActivateCoaching,
  onCancelCoaching,
  onCreditsChanged,
}: UserDetailDrawerProps) {
  const { accessToken } = useAuth();
  const [draftRole, setDraftRole] = useState<UserRole | null>(null);
  const [saving, setSaving] = useState(false);

  // Credit-Grant/Deduct — lokaler State, der die angezeigte Balance live updatet.
  const [balance, setBalance] = useState<number>(0);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditNote, setCreditNote] = useState('');
  const [creditSaving, setCreditSaving] = useState(false);
  const [creditError, setCreditError] = useState<string | null>(null);

  useEffect(() => {
    setDraftRole(user?.role ?? null);
    setBalance(user?.balance ?? 0);
    setCreditAmount('');
    setCreditNote('');
    setCreditError(null);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [user, onClose]);

  const dirty = !!user && draftRole !== null && draftRole !== user.role;

  async function save() {
    if (!user || !draftRole || !dirty) return;
    setSaving(true);
    try {
      await onSaveTier(user.id, draftRole);
    } finally {
      setSaving(false);
    }
  }

  const parsedAmount = Number.parseInt(creditAmount, 10);
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount !== 0;

  async function adjustCredits() {
    if (!user || !accessToken || !amountValid) return;
    setCreditSaving(true);
    setCreditError(null);
    try {
      const res = await adjustUserCredits(accessToken, user.id, {
        amount: parsedAmount,
        note: creditNote.trim() || undefined,
      });
      setBalance(res.balance);
      setCreditAmount('');
      setCreditNote('');
      window.dispatchEvent(new CustomEvent(CREDITS_CHANGED_EVENT));
      onCreditsChanged?.(user.id, res.balance);
    } catch (e) {
      setCreditError(e instanceof Error ? e.message : 'Buchung fehlgeschlagen');
    } finally {
      setCreditSaving(false);
    }
  }

  return (
    <AnimatePresence>
      {user ? (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 240, damping: 28 }}
            className="glass-strong fixed right-0 top-0 z-50 h-full w-[380px] overflow-y-auto border-l border-white/10 p-5"
          >
            <div className="mb-4 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="grid size-12 place-items-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-base font-bold text-neutral-950">
                  {(user.nickname ?? user.email).slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="font-mono text-[13px] text-neutral-100">{user.email}</div>
                  {user.nickname ? (
                    <div className="text-[11px] text-neutral-500">{user.nickname}</div>
                  ) : null}
                  <div className="mt-1 text-[10px] uppercase tracking-wider text-amber-300">
                    {user.tier}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1 text-neutral-500 hover:bg-white/5 hover:text-neutral-200"
              >
                <X className="size-4" />
              </button>
            </div>

            <Field label="Dabei seit" value={formatDateLong(user.created_at)} />
            <Field label="Zuletzt online" value={formatLastSeen(user.last_seen)} />
            <Field label="Disclaimer" value={formatCheck(user.disclaimer_accepted_at)} />
            <Field label="Datenschutz" value={formatCheck(user.privacy_accepted_at)} />
            <Field label="Stripe-Status" value={user.stripe_status ?? '—'} />
            <Field label="Trial-Ablauf" value={formatDateLong(user.trial_expires_at)} />
            <Field label="Bezahlt insgesamt" value={formatEuro(user.total_paid_cents)} />

            <div className="mt-5 border-t border-white/5 pt-4">
              <div className="mb-2 text-[11px] uppercase tracking-wider text-neutral-500">
                Tier ändern
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {ROLE_OPTIONS.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setDraftRole(r.key)}
                    className={`rounded-md border px-2 py-1.5 text-[12px] font-medium transition-colors ${
                      draftRole === r.key
                        ? 'border-amber-500/50 bg-amber-500/15 text-amber-200'
                        : 'border-white/10 bg-white/[0.02] text-neutral-400 hover:border-amber-500/30 hover:text-amber-300'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => void save()}
                disabled={!dirty || saving}
                className="mt-2 w-full rounded-md border border-amber-500/40 bg-amber-500/15 px-3 py-1.5 text-[12px] font-semibold text-amber-200 hover:bg-amber-500/25 disabled:opacity-40"
              >
                {saving ? 'Speichere…' : 'Änderung speichern'}
              </button>
            </div>

            <div className="mt-5 border-t border-white/5 pt-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500">Credits</div>
                <span className="font-mono text-[13px] tabular text-amber-300">
                  {balance.toLocaleString('de-DE')}
                </span>
              </div>
              <p className="mb-2 text-[10px] text-neutral-500">
                Positiver Wert = gutschreiben, negativer = abziehen (nie unter 0).
              </p>
              <input
                type="number"
                value={creditAmount}
                step={1}
                placeholder="z. B. 500 oder -200"
                onChange={(e) => setCreditAmount(e.target.value)}
                className="mb-1.5 w-full rounded-md border border-white/10 bg-white/5 px-2 py-1.5 text-[12px] tabular outline-none focus:border-amber-500/40"
                aria-label="Credit-Betrag"
              />
              <input
                type="text"
                value={creditNote}
                placeholder="Notiz (optional)"
                onChange={(e) => setCreditNote(e.target.value)}
                className="mb-1.5 w-full rounded-md border border-white/10 bg-white/5 px-2 py-1.5 text-[12px] outline-none focus:border-amber-500/40"
                aria-label="Notiz"
              />
              {creditError ? (
                <div className="mb-1.5 rounded border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-300">
                  {creditError}
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => void adjustCredits()}
                disabled={!amountValid || creditSaving}
                className="w-full rounded-md border border-amber-500/40 bg-amber-500/15 px-3 py-1.5 text-[12px] font-semibold text-amber-200 hover:bg-amber-500/25 disabled:opacity-40"
              >
                {creditSaving ? 'Buche…' : 'Gutschreiben / Abziehen'}
              </button>
            </div>

            <div className="mt-5 border-t border-white/5 pt-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500">Abo</div>
                {user.subscription_status === 'active' ? (
                  <span className="rounded border border-emerald-500/40 bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-300">
                    ACTIVE
                  </span>
                ) : user.subscription_status === 'past_due' ? (
                  <span className="rounded border border-amber-500/40 bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-300">
                    PAST DUE
                  </span>
                ) : user.subscription_status ? (
                  <span className="rounded border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] font-bold uppercase text-neutral-400">
                    {user.subscription_status}
                  </span>
                ) : (
                  <span className="text-[10px] text-neutral-600">— kein Abo</span>
                )}
              </div>
              {user.subscription_plan ? (
                <div className="flex justify-between text-[11px] text-neutral-300">
                  <span className="text-neutral-500">Plan</span>
                  <span className="font-mono">{user.subscription_plan}</span>
                </div>
              ) : null}
            </div>

            <div className="mt-5 border-t border-white/5 pt-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500">Coaching</div>
                {user.coaching_status === 'active' ? (
                  <span className="rounded border border-emerald-500/40 bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-300">
                    ACTIVE
                  </span>
                ) : user.coaching_status === 'past_due' ? (
                  <span className="rounded border border-amber-500/40 bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-300">
                    PAST DUE
                  </span>
                ) : (
                  <span className="text-[10px] text-neutral-600">— inaktiv</span>
                )}
              </div>
              {user.coaching_status === 'active' || user.coaching_status === 'past_due' ? (
                <div className="space-y-1 text-[11px] text-neutral-300">
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Plan</span>
                    <span>{user.coaching_plan}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Laeuft bis</span>
                    <span className="tabular">
                      {user.coaching_period_end
                        ? new Date(user.coaching_period_end).toLocaleDateString('de-DE')
                        : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Quelle</span>
                    <span>{user.coaching_source ?? '—'}</span>
                  </div>
                  {user.coaching_source === 'manual' ? (
                    <button
                      type="button"
                      onClick={() => void onCancelCoaching(user.id)}
                      className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-md border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-300 hover:bg-rose-500/20"
                    >
                      Pausieren
                    </button>
                  ) : (
                    <p className="mt-2 text-[10px] italic text-neutral-500">
                      Wird ueber Stripe verwaltet — Cancel ueber den Stripe-Dashboard.
                    </p>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => onActivateCoaching(user.id)}
                  className="inline-flex w-full items-center justify-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-[11px] font-medium text-amber-300 hover:bg-amber-500/20"
                >
                  + Coaching manuell aktivieren
                </button>
              )}
            </div>

            <div className="mt-5 border-t border-rose-500/10 pt-4">
              <button
                type="button"
                onClick={() => onDelete(user.id)}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-1.5 text-[12px] font-semibold text-rose-300 hover:bg-rose-500/20"
              >
                <Trash2 className="size-3.5" />
                Member entfernen
              </button>
            </div>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 py-2 text-[12px]">
      <span className="text-neutral-500">{label}</span>
      <span className="text-neutral-200 tabular">{value}</span>
    </div>
  );
}

function formatDateLong(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatLastSeen(iso: string | null): string {
  if (!iso) return 'nie';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 5 * 60_000) return 'jetzt online';
  if (diff < 60 * 60_000) return `vor ${Math.floor(diff / 60_000)} min`;
  if (diff < 24 * 3_600_000) return `vor ${Math.floor(diff / 3_600_000)} h`;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatCheck(iso: string | null): string {
  return iso ? `✓ ${formatDateLong(iso)}` : '✗ noch nicht';
}

function formatEuro(cents: number): string {
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}
